import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  FormControl,
  SimpleGrid,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import PageLoader from 'components/wholePageLoader/PageLoader';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import CommonInput from 'components/commonInput/CommonInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import {
  GetLoginUserRegionData,
  EditRegionTaxData,
  AddRegionData,
} from 'reactRedux/thunks/regionEditThunk/RegionEditThunk';
import { useDispatch, useSelector } from 'react-redux';
import CommonSubmitBtn from 'components/commonInput/CommonSubmitBtn';
import { editRegionLoginUserValidationSchema } from 'utils/CommonValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  currencyOptions,
  sideBarRoutes,
  timezoneOptions,
} from 'utils/StaticData';
import AddRegionModal from './AddRegionModal';

const RegionEdit = () => {
  const dispatch = useDispatch();
  const [isEditData, setIsEditData] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { textColor, brandStars } = useColorModeVariables();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const { isLoading, getRegionData, isEditLoading, isAddLoading } = useSelector(
    (state) => state.RegionEditSlice,
  );
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    clearErrors,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(editRegionLoginUserValidationSchema(isEditData)),
    key: String(isEditData),
  });

  const { fields, replace } = useFieldArray({
    control,
    name: 'regions',
  });

  useEffect(() => {
    dispatch(GetLoginUserRegionData());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(getRegionData) && getRegionData?.length) {
      replace(
        getRegionData.map((region) => ({
          ...region,
          timezone: region.timezone
            ? { id: region.timezone, label: region.timezone }
            : null,
          currencyOption: region.currency
            ? (currencyOptions.find((c) => c.code === region.currency) ?? {
                code: region.currency,
                symbol: region.currencySymbol ?? region.currency,
                label: region.currency,
              })
            : currencyOptions[0],
        })),
      );
    }
  }, [isEditData, getRegionData, replace]);

  const onSubmit = (data) => {
    const updatedRegions = data?.regions
      ?.filter((region, index) => {
        const original = getRegionData[index];
        return (
          Number(region.tax) !== Number(original.tax) ||
          Number(region.lostCharge) !== Number(original.lostCharge) ||
          Number(region.profitShare) !== Number(original.profitShare) ||
          region.settlementDays !== original.settlementDays ||
          region.orgName !== original.orgName ||
          region.orgAddress !== original.orgAddress ||
          region.orgCode !== original.orgCode ||
          region.landmark !== original.landmark ||
          Number(region.eInvoiceFee) !== Number(original.eInvoiceFee) ||
          region.timezone?.id !== original.timezone ||
          region.currencyOption?.code !== original.currency ||
          region.currencyOption?.symbol !== original.currencySymbol
        );
      })
      ?.map((region) => {
        const combinedAddress = `${region.orgName} - ${region.orgAddress} - ${region.orgCode}`;
        return {
          regionId: region.regionId,
          tax: region.tax,
          lostCharge: Number(region.lostCharge),
          profitShare: Number(region.profitShare),
          settlementDays: region.settlementDays,
          orgName: region.orgName,
          orgAddress: region.orgAddress,
          orgCode: region.orgCode,
          address: combinedAddress,
          landmark: region.landmark,
          eInvoiceFee: region.eInvoiceFee,
          ...(region.timezone?.id && { timezone: region.timezone.id }),
          ...(region.currencyOption?.code && {
            currency: region.currencyOption.code,
          }),
          ...(region.currencyOption?.symbol && {
            currencySymbol: region.currencyOption.symbol,
          }),
        };
      });
    const payload = { regions: updatedRegions };
    if (updatedRegions.length > 0) {
      dispatch(
        EditRegionTaxData(payload, () => {
          setIsEditData(true);
          dispatch(GetLoginUserRegionData());
        }),
      );
    }
  };

  const handleToggleEdit = () => {
    const newIsEditData = !isEditData;
    setIsEditData(newIsEditData);
    if (newIsEditData) {
      clearErrors();
    }
  };

  const handleAddRegion = (payload, resetForm) => {
    dispatch(
      AddRegionData(payload, () => {
        resetForm();
        onClose();
        dispatch(GetLoginUserRegionData());
      }),
    );
  };

  return (
    <>
      <Box pt={{ base: '60px', md: '80px', xl: '78px' }}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="2xl" fontWeight="bold">
            Regional settings
          </Text>

          <Flex gap={3}>
            <Button
              type="button"
              bg="reviveOrange"
              h="50"
              color="primary"
              borderRadius="12px"
              _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
              onClick={onOpen}
            >
              Add
            </Button>

            <Box onClick={() => setIsEditData(!isEditData)}>
              <Button
                type="button"
                bg="reviveOrange"
                h="50"
                color="primary"
                borderRadius="12px"
                _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                onClick={handleToggleEdit}
              >
                {isEditData ? `Update` : 'Cancel'}
              </Button>
            </Box>
          </Flex>
        </Flex>

        <Flex direction="column" align="center" justify="center">
          <Card
            w="100%"
            boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
            borderRadius="20px"
            paddingInline="40px"
            paddingBlock="50px"
            overflowX={{ sm: 'scroll', lg: 'hidden' }}
          >
            {isLoading ? (
              <PageLoader />
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <FormControl>
                  {fields?.map((item, index) => (
                    <>
                      <Text
                        fontSize="lg"
                        fontWeight={700}
                        mb={4}
                        color="gray.900"
                      >
                        {fields?.[index]?.regionName}
                      </Text>
                      <SimpleGrid
                        key={item.id}
                        columns={{
                          base: 1,
                          sm: 1,
                          md: 2,
                          lg: 3,
                          xl: 4,
                        }}
                        spacing="24px"
                        mb="20px"
                      >
                        {/* Region ID */}
                        <CommonInput
                          type="text"
                          name={`regions.${index}.regionId`}
                          labelText="Region ID"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Region ID"
                          register={register(`regions.${index}.regionId`)}
                          readOnly={true}
                        />

                        {/* Tax Input */}
                        <CommonInput
                          type="number"
                          name={`regions.${index}.tax`}
                          labelText="Tax"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Tax"
                          register={register(`regions.${index}.tax`)}
                          errors={errors?.regions?.[index]?.tax}
                          isValidate={true}
                          isAddPercentage={true}
                          readOnly={isEditData}
                        />

                        {/* lost_Charge */}
                        <CommonInput
                          type="number"
                          name={`regions.${index}.lostCharge`}
                          labelText="Lost Charge"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Lost Charge"
                          register={register(`regions.${index}.lostCharge`)}
                          errors={errors?.regions?.[index]?.lostCharge}
                          isValidate={true}
                          readOnly={isEditData}
                        />

                        {adminDetails?.role === sideBarRoutes?.Admin && (
                          <CommonInput
                            type="number"
                            name={`regions.${index}.profitShare`}
                            labelText="Profit Sharing"
                            textColor={textColor}
                            brandStars={brandStars}
                            placeholder="Profit Sharing"
                            register={register(`regions.${index}.profitShare`)}
                            errors={errors?.regions?.[index]?.profitShare}
                            isValidate={true}
                            isAddPercentage={true}
                            readOnly={isEditData}
                          />
                        )}

                        {/* Settlement Days */}
                        <CommonInput
                          type="number"
                          name={`regions.${index}.settlementDays`}
                          labelText="Settlement Days"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Settlement Days"
                          register={register(`regions.${index}.settlementDays`)}
                          errors={errors?.regions?.[index]?.settlementDays}
                          isValidate={true}
                          readOnly={isEditData}
                        />

                        {/* Organization Name */}
                        <CommonInput
                          type="text"
                          name={`regions.${index}.orgName`}
                          labelText="Organization Name"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Organization Name"
                          register={register(`regions.${index}.orgName`)}
                          errors={errors?.regions?.[index]?.orgName}
                          isValidate={true}
                          readOnly={isEditData}
                        />

                        {/* Organization Address */}
                        <CommonInput
                          type="text"
                          name={`regions.${index}.orgAddress`}
                          labelText="Organization Address"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Organization Address"
                          register={register(`regions.${index}.orgAddress`)}
                          errors={errors?.regions?.[index]?.orgAddress}
                          isValidate={true}
                          readOnly={isEditData}
                        />

                        {/* Organization Code */}
                        <CommonInput
                          type="text"
                          name={`regions.${index}.orgCode`}
                          labelText="Organization Code"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Organization Code"
                          register={register(`regions.${index}.orgCode`)}
                          errors={errors?.regions?.[index]?.orgCode}
                          isValidate={true}
                          readOnly={isEditData}
                        />

                        {/* Landmark */}
                        {(adminDetails?.role === sideBarRoutes?.Admin ||
                          adminDetails?.role === sideBarRoutes?.Dealer) && (
                          <CommonInput
                            type="text"
                            name={`regions.${index}.landmark`}
                            labelText="Invoice Generated"
                            textColor={textColor}
                            brandStars={brandStars}
                            placeholder="Invoice Generated"
                            register={register(`regions.${index}.landmark`)}
                            errors={errors?.regions?.[index]?.landmark}
                            isValidate={true}
                            readOnly={isEditData}
                          />
                        )}

                        {/* E-Invoice Fee */}
                        <CommonInput
                          type="number"
                          name={`regions.${index}.eInvoiceFee`}
                          labelText="E-Invoice Fee"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="E-Invoice Fee"
                          register={register(`regions.${index}.eInvoiceFee`)}
                          errors={errors?.regions?.[index]?.eInvoiceFee}
                          isValidate={true}
                          readOnly={isEditData}
                        />

                        {/* Timezone */}
                        <Controller
                          name={`regions.${index}.timezone`}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <CommonReactSelect
                              label="Timezone"
                              name={`regions.${index}.timezone`}
                              type="select"
                              options={timezoneOptions}
                              value={value}
                              onChange={onChange}
                              register={register}
                              isRequired={true}
                              error={errors?.regions?.[index]?.timezone}
                              getOptionLabel={(e) => e?.label}
                              getOptionValue={(e) => e?.id}
                              isDisabled={isEditData}
                            />
                          )}
                        />

                        {/* Currency */}
                        <Controller
                          name={`regions.${index}.currencyOption`}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <CommonReactSelect
                              label="Currency"
                              name={`regions.${index}.currencyOption`}
                              type="select"
                              options={currencyOptions}
                              value={value}
                              onChange={onChange}
                              register={register}
                              isRequired={false}
                              error={errors?.regions?.[index]?.currencyOption}
                              getOptionLabel={(e) => e?.label}
                              getOptionValue={(e) => e?.code}
                              isDisabled={isEditData}
                            />
                          )}
                        />
                      </SimpleGrid>
                    </>
                  ))}

                  {!isEditData && (
                    <>
                      <SimpleGrid
                        columns={{ base: 1, md: 2, lg: 3 }}
                        spacing="24px"
                      >
                        <CommonSubmitBtn
                          BTNtext="Submit"
                          isLoading={isEditLoading}
                        />
                      </SimpleGrid>
                    </>
                  )}
                </FormControl>
              </form>
            )}
          </Card>
        </Flex>

        <AddRegionModal
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleAddRegion}
          isLoading={isAddLoading}
          adminDetails={adminDetails}
        />
      </Box>
    </>
  );
};

export default RegionEdit;
